export class WorkorderModel {
  wo_header: WoHeader;
  wo_items?: (WoItemsEntity)[] | null;
  wo_hours?: (WoHoursEntity)[] | null;
  wo_attachments?: (WoAttachmentsEntity)[] | null;
  wo_notes?: (WoNotesEntity)[] | null;
  wo_travel?: (WoTravelEntity)[] | null;
}

export class WoHeader {
  id: string;
  entity_id: string;
  address_id: string;
  contact_id: string;
  company_id: string;
  status: string;
  work_order_date: string;
  required_by_date: string;
  next_visit_date: string;
  completed_date: string;
  assigned_user_id: string;
  number: string;
  description: string;
  dispatch_notes: string;
  signature: string;
  audit_sequence: number;
}

export class WoItemsEntity {
  id: string;
  header_id: string;
  item_id: string;
  quantity: number;
  price: number;
  sort_order: number;
}

export class WoHoursEntity {
  id: string;
  header_id: string;
  user_id: string;
  date: string;
  hours: number;
  minutes: number;
  description: string;
  sort_order: number;
}

export class WoAttachmentsEntity {
  id: string;
  header_id: string;
  attachment: string;
  type: string;
}

export class WoNotesEntity {
  id: string;
  header_id: string;
  note: string;
  date: string;
  sort_order: number;
}

export class WoTravelEntity {
  id: string;
  header_id: string;
  user_id: string;
  distance: number;
  uom: string;
  sort_order: number;
  description: string;
  date: string;
  // Refactor additions
  unit_type: string | null;         // 'miles' | 'km'
  rate_per_unit: number | null;
  billable: boolean;
  billed_amount: number | null;     // computed server-side
  invoice_detail_id: string | null;
}

// ─── WoWorkType hardcoded enum ────────────────────────────────────────────────

export const WO_WORK_TYPES = [
  {value: 'regular', label: 'Regular', isPremium: false},
  {value: 'overtime', label: 'Overtime', isPremium: true},
  {value: 'after_hours', label: 'After Hours', isPremium: true},
  {value: 'holiday', label: 'Holiday', isPremium: true},
  {value: 'emergency', label: 'Emergency', isPremium: true},
  {value: 'warranty', label: 'Warranty', isPremium: false},
  {value: 'training', label: 'Training', isPremium: false},
] as const;

export type WoWorkType = typeof WO_WORK_TYPES[number]['value'];
// 'regular' | 'overtime' | 'after_hours' | 'holiday' | 'emergency' | 'warranty' | 'training'

// ─── Refactor additions to existing entities ──────────────────────────────────

// Extend WoHoursEntity with new billing fields (used in refactored form/tabs)
export interface WoHoursRefactor {
  work_type: WoWorkType | null;
  billing_rate: number | null;
  billable: boolean;
  billed_amount: number | null;     // computed server-side
  invoice_detail_id: string | null;
}

// Extend WoItemsEntity with new billing fields
export interface WoItemsRefactor {
  unit_cost: number | null;
  billable: boolean;
  invoice_detail_id: string | null;
}

// ─── New interfaces ───────────────────────────────────────────────────────────

export interface WoType {
  id: string;
  name: string;
  default_billing_rate: number | null;
  default_sla_response_hours: number | null;
  default_sla_resolution_hours: number | null;
  active: boolean;
  sort_order: number;
}

export interface WoCategory {
  id: string;
  name: string;
  active: boolean;
  sort_order: number;
}

export interface WoAsset {
  id: string;
  entity_id: string;
  location_id: string | null;
  name: string;
  asset_tag: string | null;
  serial_number: string | null;
  model: string | null;
  manufacturer: string | null;
  install_date: string | null;
  warranty_expiry: string | null;
  last_service_date: string | null;
  next_service_date: string | null;
  notes: string | null;
  active: boolean;
}

export interface WoRecurringSchedule {
  id: string;
  entity_id: string;
  asset_id: string | null;
  company_id: string;
  type_id: string;
  description: string;
  frequency: string;          // 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually'
  frequency_interval: number;
  next_due_date: string;
  last_generated_date: string | null;
  assigned_user_id: string | null;
  estimated_hours: number | null;
  active: boolean;
}

export interface WoUserBillingRate {
  id: string;
  user_id: string;
  work_type: WoWorkType;
  base_rate: number;
  rate_modifier: number;      // e.g. 1.50 for time-and-a-half
  effective_rate: number;     // computed: base_rate × rate_modifier
  active: boolean;
}

export interface WoHoursBillingLine {
  id: string;
  date: string;
  description: string;
  hours: number;
  billing_rate: number;
  billed_amount: number | null;
  billable: boolean;
  invoiced: boolean;
  invoice_detail_id?: string | null;
  user_id?: string | null;
  work_type?: string | null;
}

export interface WoTravelBillingLine {
  id: string;
  date: string;
  description: string;
  units: number;
  unit_type: string;
  rate_per_unit: number;
  billed_amount: number | null;
  billable: boolean;
  invoiced: boolean;
  invoice_detail_id?: string | null;
}

export interface WoMaterialBillingLine {
  id: string;
  date: string | null;
  item_id: string | null;
  item_name: string;
  quantity: number;
  unit_price: number;
  billed_amount: number | null;
  billable: boolean;
  invoiced: boolean;
  invoice_detail_id?: string | null;
}

/** @deprecated Use WoHoursBillingLine | WoTravelBillingLine | WoMaterialBillingLine */
export interface WoBillingLine extends WoHoursBillingLine {
  rate: number;
  quantity: number;
}

export interface WoLinkedInvoice {
  invoice_id: string;
  invoice_number: string;
  total: number;
  date: string;
}

export interface WoInvoiceLine {
  id: string;
  item_id: string;
}

export interface WoBillingSummary {
  work_order_id: string;
  work_order_number: string;
  entity_name: string;
  billing_status: string;
  hours: WoHoursBillingLine[];
  travel: WoTravelBillingLine[];
  materials: WoMaterialBillingLine[];
  totals: {
    hours_total: number;
    travel_total: number;
    materials_total: number;
    grand_total: number;
  };
}

export interface WoDashboard {
  as_of: string;
  summary: {
    open: number;
    dispatched: number;
    in_progress: number;
    completed_today: number;
    overdue: number;
    unbilled: number;
    sla_breached: number;
  };
  by_priority: { critical: number; high: number; normal: number; low: number; };
  by_type: { type_name: string; count: number; }[];
  by_technician: { user_name: string; open: number; completed_today: number; }[];
  overdue_work_orders: any[];
  upcoming_warranty_expiry: {
    asset_id: string;
    asset_name: string;
    entity_name: string;
    warranty_expiry: string;
    days_remaining: number;
  }[];
  upcoming_pm: any[];
}
